import { useCallback, useState } from 'react';
import { serverEndpoints } from '@/config/serverEndpoints';

/**
 * Props for the CopyableIp component.
 *
 * @interface CopyableIpProps
 * @property {string} [address] - The server address to display and copy. Defaults to the live direct-connect IP.
 * @property {string} [className] - Additional CSS classes applied to the button wrapper.
 */
interface CopyableIpProps {
  address?: string;
  className?: string;
}

/**
 * Inline server IP that copies itself to the clipboard when clicked.
 *
 * @description Renders the server address as a clickable button. Clicking copies the
 * address to the clipboard (with a fallback for non-secure contexts / older browsers)
 * and briefly swaps the copy icon for a confirmation. Used everywhere the server IP
 * is shown so players can grab the connect string in one click.
 *
 * @param {CopyableIpProps} props - The component props
 * @param {string} [props.address=serverEndpoints.direct.address] - Address to display and copy
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {JSX.Element} A clickable, copyable server IP
 */
export default function CopyableIp({
  address = serverEndpoints.direct.address,
  className = '',
}: CopyableIpProps) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(address);
    } catch {
      // Fallback for non-secure (http) contexts and older browsers.
      const el = document.createElement('textarea');
      el.value = address;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      try {
        document.execCommand('copy');
      } catch {
        /* clipboard unavailable */
      }
      document.body.removeChild(el);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }, [address]);

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied!' : 'Click to copy server IP'}
      aria-label={copied ? `Server IP ${address} copied to clipboard` : `Copy server IP ${address}`}
      className={`group inline-flex items-center gap-1.5 font-mono cursor-pointer transition-colors ${className}`}
    >
      <span>{address}</span>
      <i
        className={copied ? 'ri-check-line text-green-400' : 'ri-file-copy-line opacity-60 group-hover:opacity-100'}
        aria-hidden="true"
      ></i>
      {copied && <span className="font-sans text-xs text-green-400">Copied!</span>}
    </button>
  );
}
